import { cookies, removeCookie } from "sharedKernel/cookies/behavior";
import { getCredLocalStorage } from "sharedKernel/auth/credLocalStorage";
import { getStartRoute, getHubRoute } from "sharedKernel/handlers";
import { trans } from "sharedKernel/i18n";
import { changeMenu, changeVisibleRows, store } from "store";
import { IMenuEvaluators } from "../state/content/store/contracts";
import { showModal } from "components/popup";
import React from "react";
import { EvaluationModal } from "domain/feature/hub/evaluator/challenge/EvaluationModal";
import { ROUTES } from "sharedKernel/constants";
import { canAccessMeetingRoom } from "sharedKernel/auth/behavior";
import { caixaUser } from "sharedKernel/user/CaixaUser";
import { icon, library } from "@fortawesome/fontawesome-svg-core";
import {
  faChartSimple,
  faChevronRight,
  faDoorOpen,
  faTicket,
  faTowerBroadcast,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";

library.add(
  faChartSimple,
  faChevronRight,
  faDoorOpen,
  faTicket,
  faTowerBroadcast,
  faUserGroup
);

const setshowGoBackMenuItem = (_features): boolean => {
  return false;
};

const getSolutionTitle = (hide: boolean, solution: any): string => {
  if (hide) {
    return "Empresa oculta";
  } else {
    return solution.title;
  }
};

const setGoBackRoute = (features): string => {
  const permissions: string[] = [];
  features?.map((feature, index): boolean => {
    permissions.push(String(feature.code));
    return true;
  });

  return getStartRoute(permissions);
};

const handleVisibleRows = (
  rowIndexString: string,
  visibleValues: string[]
): void => {
  if (visibleValues.includes(rowIndexString)) {
    visibleValues = visibleValues.filter((value) => value !== rowIndexString);
  } else if (rowIndexString !== null) {
    visibleValues.push(rowIndexString);
  }

  store.dispatch(changeVisibleRows(visibleValues));
};

const removeDuplicatedMenuItems = (items: any[]): any[] => {
  return items.filter(
    (item, index, self) =>
      index ===
      self.findIndex((candidate) => {
        return candidate?.path === item?.path;
      })
  );
};

const meetingRoomAccordionActive = (currentLocation?: string): boolean =>
  Boolean(currentLocation?.includes("/hub/company/meeting-room"));

type MenuAccess = {
  hasCompany: boolean;
  hasStartup: boolean;
  hasEvaluator?: boolean;
};

type CategoryAccordionSubItem = {
  title: string;
  path: string;
  active?: boolean;
  onClick?: () => void;
};

const buildMenuAccess = (
  feature?: string[],
  overrides: Partial<MenuAccess> = {}
): MenuAccess => ({
  hasCompany: feature?.includes("challenge") ?? false,
  hasStartup: feature?.includes("idea") ?? false,
  hasEvaluator: feature?.includes("evaluation") ?? false,
  ...overrides,
});

const createAccordionItem = (
  title: string,
  icon: [string, string],
  pathAnchor: string,
  subItems: CategoryAccordionSubItem[]
): any | null => {
  if (subItems.length === 0) {
    return null;
  }

  return {
    title,
    icon,
    active: subItems.some((item) => item.active),
    path: pathAnchor,
    isSubMenu: true,
    isAccordion: true,
    subItems,
  };
};

const pushCategorySubItems = (
  items: CategoryAccordionSubItem[],
  access: MenuAccess,
  manage: CategoryAccordionSubItem,
  available: CategoryAccordionSubItem
): void => {
  if (access.hasCompany) {
    items.push(manage);
  }
  if (access.hasStartup) {
    items.push(available);
  }
};

const buildCategoryAccordions = (
  currentLocation: string | undefined,
  access: MenuAccess
): any[] => {
  const accordions: any[] = [];

  const ecossistemaSubItems: CategoryAccordionSubItem[] = [];
  if (access.hasCompany) {
    ecossistemaSubItems.push({
      title: trans("Gerenciar Ecossistema"),
      path: getHubRoute(ROUTES.HUB.COMPANY.ECOSYSTEM),
      active: currentLocation?.includes("/hub/company/ecosystem"),
    });
  } else if (access.hasStartup) {
    ecossistemaSubItems.push({
      title: trans("Ecossistema"),
      path: getHubRoute(ROUTES.HUB.COMPANY.ECOSYSTEM),
      active: currentLocation?.includes("/hub/company/ecosystem"),
    });
  }
  const ecossistema = createAccordionItem(
    trans("Ecossistema"),
    ["fas", "user-group"],
    "#ecossistema",
    ecossistemaSubItems
  );
  if (ecossistema) {
    accordions.push(ecossistema);
  }

  const programasSubItems: CategoryAccordionSubItem[] = [];
  pushCategorySubItems(
    programasSubItems,
    access,
    {
      title: trans("Gerenciar programas"),
      path: getHubRoute(ROUTES.HUB.COMPANY.CHALLENGE_LIST),
      active: currentLocation?.includes("/hub/company/challenge"),
    },
    {
      title: trans("Programas disponíveis"),
      path: getHubRoute(ROUTES.HUB.STARTUP.CHALLENGE_LIST),
      active: currentLocation?.includes("/hub/startup/challenge"),
    }
  );
  if (access.hasStartup) {
    programasSubItems.push({
      title: trans("Minhas inscrições"),
      path: getHubRoute(ROUTES.HUB.STARTUP.IDEA_LIST),
      active: currentLocation?.includes("/hub/startup/idea"),
    });
  }
  if (access.hasEvaluator) {
    programasSubItems.push({
      title: trans("Minhas avaliações"),
      path: getHubRoute(ROUTES.HUB.EVALUATOR.CHALLENGE_LIST),
      active: currentLocation?.includes("/hub/evaluator/challenge"),
    });
  }
  const programas = createAccordionItem(
    trans("Programas"),
    ["fas", "puzzle-piece"],
    "#programas",
    programasSubItems
  );
  if (programas) {
    accordions.push(programas);
  }

  const eventosSubItems: CategoryAccordionSubItem[] = [];
  pushCategorySubItems(
    eventosSubItems,
    access,
    {
      title: trans("Gerenciar eventos"),
      path: getHubRoute(ROUTES.HUB.COMPANY.EVENTS_LIST),
      active: currentLocation?.includes("/hub/company/event"),
    },
    {
      title: trans("Eventos disponíveis"),
      path: getHubRoute(ROUTES.HUB.STARTUP.EVENTS_LIST),
      active: currentLocation?.includes("/hub/startup/event"),
    }
  );
  const eventos = createAccordionItem(
    trans("Eventos"),
    ["fas", "ticket"],
    "#eventos",
    eventosSubItems
  );
  if (eventos) {
    accordions.push(eventos);
  }

  const oportunidadesSubItems: CategoryAccordionSubItem[] = [];
  pushCategorySubItems(
    oportunidadesSubItems,
    access,
    {
      title: trans("Gerenciar oportunidades"),
      path: getHubRoute(ROUTES.HUB.COMPANY.SCOUTING_LIST),
      active: currentLocation?.includes("/hub/company/scouting"),
    },
    {
      title: trans("Oportunidades disponíveis"),
      path: getHubRoute(ROUTES.HUB.STARTUP.OPPORTUNITY_LIST),
      active: currentLocation?.includes("/hub/startup/opportunity"),
    }
  );
  const oportunidades = createAccordionItem(
    trans("Oportunidades"),
    ["fas", "tower-broadcast"],
    "#oportunidades",
    oportunidadesSubItems
  );
  if (oportunidades) {
    accordions.push(oportunidades);
  }

  const trilhasSubItems: CategoryAccordionSubItem[] = [];
  pushCategorySubItems(
    trilhasSubItems,
    access,
    {
      title: trans("Gerenciar trilhas"),
      path: getHubRoute(ROUTES.HUB.TRACK.TRACKS_LIST),
      active: Boolean(
        currentLocation?.includes("/hub/tracks") &&
          !currentLocation?.includes("/hub/available-tracks")
      ),
    },
    {
      title: trans("Trilhas disponíveis"),
      path: getHubRoute(ROUTES.HUB.TRACK.TRACKS_AVAILABLE),
      active: currentLocation?.includes("/hub/available-tracks"),
    }
  );
  if (access.hasStartup) {
    trilhasSubItems.push({
      title: trans("Meu aprendizado"),
      path: getHubRoute(ROUTES.HUB.TRACK.LEARNING),
      active: currentLocation?.includes("/hub/learning"),
    });
  }
  const trilhas = createAccordionItem(
    trans("Trilhas"),
    ["fas", "graduation-cap"],
    "#trilhas",
    trilhasSubItems
  );
  if (trilhas) {
    accordions.push(trilhas);
  }

  return accordions;
};

const getMeetingRoomAccordion = (
  currentLocation: string | undefined,
  variant: "company" | "shared"
): any => {
  const sharedSubItems: CategoryAccordionSubItem[] = [];

  const companyOnlySubItems = [
    {
      title: trans("Gerenciar Espaços"),
      path: getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE),
      active: Boolean(
        currentLocation?.endsWith(ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE) ||
          currentLocation?.includes(
            `${ROUTES.HUB.COMPANY.MEETING_ROOM_MANAGE}/new`
          ) ||
          /\/meeting-room\/manage-meeting-room\/[^/]+\/edit/.test(
            currentLocation ?? ""
          )
      ),
    },
    {
      title: trans("Espaços reservados"),
      path: getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_RESERVED),
      active: currentLocation?.includes(
        ROUTES.HUB.COMPANY.MEETING_ROOM_RESERVED
      ),
    },
    {
      title: trans("Gerenciar solicitações"),
      path: getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_REQUESTS),
      active: currentLocation?.includes(
        ROUTES.HUB.COMPANY.MEETING_ROOM_REQUESTS
      ),
    },
  ];

  const subItems =
    variant === "company"
      ? [...companyOnlySubItems, ...sharedSubItems]
      : sharedSubItems;

  if (subItems.length === 0) {
    return null;
  }

  return {
    title: trans("Reserva de espaços"),
    icon: ["fas", "door-open"],
    active: meetingRoomAccordionActive(currentLocation),
    path: "#reserva-de-espacos",
    isSubMenu: true,
    isAccordion: true,
    subItems,
  };
};

export function createMenu(
  currentLocation?: string,
  feature?: string[],
  caixaRole?: "solucionador" | "avaliador"
): any[] {
  const features = cookies.get("_perm")?.features;
  const logged = getCredLocalStorage();
  const Companytype = logged?.company?.type?.type;
  const state = store.getState();

  // Fall back to cookie when Redux permissions haven't loaded yet (e.g. after a hard
  // redirect). `_permCodes` is the tiny companion cookie that still persists when the
  // full `_perm` cookie was dropped for exceeding the per-cookie size limit.
  const effectiveFeature: string[] =
    feature && feature.length > 0
      ? feature
      : features?.map((f: any) => String(f.code)) ??
        (cookies.get("_permCodes") as string[] | undefined) ??
        [];

  const showMeetingRoomMenu = canAccessMeetingRoom();
  const meetingRoomCompanyItems = showMeetingRoomMenu
    ? [getMeetingRoomAccordion(currentLocation, "company")].filter(Boolean)
    : [];
  const meetingRoomSharedItems = showMeetingRoomMenu
    ? [getMeetingRoomAccordion(currentLocation, "shared")].filter(Boolean)
    : [];

  const menuEvaluatorsState: IMenuEvaluators = state.menuEvaluatorsState;
  const menuEvaluators = menuEvaluatorsState.rows;

  const menuEvaluatorsItems: any[] = [
    {
      title: trans("Voltar"),
      icon: ["fas", "fa-reply"],
      path: setGoBackRoute(features),
      onClick: () => {
        store.dispatch(changeMenu(false));
        store.dispatch(changeVisibleRows([]));
      },
    },
    {
      title: trans("Minhas avaliações"),
      icon: ["fas", "clipboard-check"],
      active: currentLocation?.includes("/hub/evaluator/challenge"),
      path: getHubRoute(ROUTES.HUB.EVALUATOR.CHALLENGE_LIST),
      onClick: () => {
        store.dispatch(changeVisibleRows([]));
      },
    },
  ];
  menuEvaluators?.forEach((row, rowIndex) => {
    const evaluationStartDate = new Date(row.config.step.evaluationStartDate);
    const evaluationEndDate = new Date(row.config.step.closingDate);
    const today = new Date(Date.now());
    const availableToEvaluate =
      evaluationStartDate <= today && today <= evaluationEndDate;
    const solutions: any[] = [];
    const rowIndexString = rowIndex.toString();
    row.evaluation.steps.forEach((step) => {
      step.resolvers.forEach((resolver) => {
        resolver.solutions.forEach((solution) => {
          solutions.push({
            title: getSolutionTitle(row.config.hideIdeaTitle, solution),
            path: `/hub/evaluator/challenge/${row.id}/step/${step.id}/idea/${solution.id}`,
            tooltipContent: getSolutionTitle(
              row.config.hideIdeaTitle,
              solution
            ),
            grade: solution.points !== 0 ? solution.points : 0.0,
            fillPercentage: solution.evaluationFillPercentage,
            disabled: !availableToEvaluate,
            onGradeClick: () => {
              const idList = {
                challengeId: row.id,
                stepId: step.id,
                ideaId: solution.id,
              };
              showModal("evaluationModal", {
                title: solution.title,
                component: <EvaluationModal disabled={!availableToEvaluate} />,
                buttons: [
                  { text: "Salvar", value: "labels.confirm.value" },
                  { text: "Cancelar" },
                ],
                data: idList,
                isGetFromRedux: false,
              });
            },
          });
        });
      });
    });
    menuEvaluatorsItems.push({
      title: row.config.title,
      icon: ["fas", "puzzle-piece"],
      path: window.location.pathname,
      active: currentLocation?.includes(`${row.id}`),
      isSubMenu: true,
      subMenuItems: solutions,
      accordionOpen:
        state.isEvaluationMenu.visibleRows.includes(rowIndexString),
      onClick: () => {
        handleVisibleRows(rowIndexString, state.isEvaluationMenu.visibleRows);
      },
    });
  });

  const showGoBackMenuItem = setshowGoBackMenuItem(features);
  const goBackRoute = setGoBackRoute(features);
  const goBack = showGoBackMenuItem
    ? [{ title: trans("Voltar"), icon: ["fas", "fa-reply"], path: goBackRoute }]
    : [];

  const dashboardMenu = [
    {
      title: trans("Visão geral"),
      icon: ["fas", "fa-chart-bar"],
      active: currentLocation?.includes("/hub/dashboard"),
      path: getHubRoute(ROUTES.HUB.DASHBOARD),
    },
  ];

  const companyMenuBase = [
    ...dashboardMenu,
    {
      title: trans("Calendário"),
      icon: ["fas", "calendar"],
      active: currentLocation?.includes("hub/company/calendar"),
      path: getHubRoute(ROUTES.HUB.COMPANY.CALENDAR),
    },
    ...buildCategoryAccordions(
      currentLocation,
      buildMenuAccess(effectiveFeature, { hasCompany: true, hasStartup: false })
    ),
  ];

  const companyMenu: any[] = [
    ...companyMenuBase,
    ...meetingRoomCompanyItems,
  ];

  const startupMenuCore = [
    ...buildCategoryAccordions(
      currentLocation,
      buildMenuAccess(effectiveFeature, { hasCompany: false, hasStartup: true })
    ),
  ];

  const startupMenu = [
    ...startupMenuCore,
    ...meetingRoomSharedItems,
  ];

  const caixaSolucionadorMenu = [
    ...buildCategoryAccordions(
      currentLocation,
      buildMenuAccess(effectiveFeature, { hasCompany: false, hasStartup: true })
    ),
    ...meetingRoomSharedItems,
  ];

  const caixaAvaliadorMenu = [
    ...buildCategoryAccordions(
      currentLocation,
      buildMenuAccess(effectiveFeature, {
        hasCompany: false,
        hasStartup: false,
        hasEvaluator: true,
      })
    ),
  ];

  const adminCityHallMenu = [
    ...buildCategoryAccordions(
      currentLocation,
      buildMenuAccess(effectiveFeature, { hasCompany: false, hasStartup: true })
    ),
    ...meetingRoomSharedItems,
  ];

  const cityHallMenu = [
    ...buildCategoryAccordions(
      currentLocation,
      buildMenuAccess(effectiveFeature, { hasCompany: false, hasStartup: true })
    ),
    ...meetingRoomSharedItems,
  ];

  const configMenu = [
    {
      title: trans("Configurações"),
      icon: ["fas", "wrench"],
      active: currentLocation?.includes("/hub/user-config"),
      path: getHubRoute(ROUTES.HUB.USER_CONFIG),
    },
  ];

  const publicAdministrationMenu = [
    {
      title: trans("Adicionar membros"),
      icon: ["fas", "user-plus"],
      active: currentLocation?.includes("/hub/public-administration"),
      path: getHubRoute(ROUTES.HUB.PUBLIC_ADMINISTRATION),
    },
  ];

  let menuItems: any = [...goBack];

  const isCaixaUser = caixaUser.isCaixaUser();
  const hasCaixaPermission = effectiveFeature.includes("caixa_employee");

  const companyType = logged?.company?.type;
  const hasAdminCityHallPermission = Companytype === "Admin City Hall";
  const hasCityHallPermission = Companytype === "City Hall";
  const hasPublicAdministrationPermission = Companytype === "Public Administration";
  const hasResolverPermission = Companytype === "Resolver";

  if (isCaixaUser || hasCaixaPermission) {
    if (caixaRole === "avaliador") {
      menuItems = [...menuItems, ...caixaAvaliadorMenu];
    } else {
      menuItems = [...menuItems, ...caixaSolucionadorMenu];
    }

    menuItems = [...menuItems, ...configMenu];
  }
  else if (hasAdminCityHallPermission) {
    menuItems = [...menuItems, ...adminCityHallMenu];
    menuItems = [...menuItems, ...configMenu];
  } else if (hasCityHallPermission) {
    menuItems = [...menuItems, ...cityHallMenu];
    menuItems = [...menuItems, ...configMenu];
  } else if (hasPublicAdministrationPermission || hasResolverPermission) {
    const hasStartup = effectiveFeature.includes("idea");

    if (hasStartup) {
      menuItems = [...menuItems, ...startupMenu];
    }

    menuItems = [
      ...menuItems,
      ...publicAdministrationMenu,
      ...configMenu,
    ];
  } else {
    const hasCompany = effectiveFeature.includes("challenge");
    const hasStartup = effectiveFeature.includes("idea");

    if (hasCompany && hasStartup) {
      menuItems = [
        ...menuItems,
        ...dashboardMenu,
        {
          title: trans("Calendário"),
          icon: ["fas", "calendar"],
          active: currentLocation?.includes("hub/company/calendar"),
          path: getHubRoute(ROUTES.HUB.COMPANY.CALENDAR),
        },
        ...buildCategoryAccordions(
          currentLocation,
          buildMenuAccess(effectiveFeature, { hasCompany: true, hasStartup: true })
        ),
        ...meetingRoomCompanyItems,
      ];
    } else {
      if (hasCompany) {
        menuItems = [...menuItems, ...companyMenu];
      }

      if (hasStartup) {
        menuItems = [...menuItems, ...startupMenu];
      }
    }

    const hasEvaluation = effectiveFeature.includes("evaluation");
    if (hasEvaluation && !hasCompany && !hasStartup) {
      menuItems = [
        ...menuItems,
        ...buildCategoryAccordions(
          currentLocation,
          buildMenuAccess(effectiveFeature, {
            hasCompany: false,
            hasStartup: false,
            hasEvaluator: true,
          })
        ),
      ];
    }

    menuItems = [...menuItems, ...configMenu];
  }

  if (logged?.isSuperAdmin) {
    menuItems = [
      ...dashboardMenu,
      {
        title: trans("Calendário"),
        icon: ["fas", "calendar"],
        active: currentLocation?.includes("hub/company/calendar"),
        path: getHubRoute(ROUTES.HUB.COMPANY.CALENDAR),
      },
      ...buildCategoryAccordions(
        currentLocation,
        buildMenuAccess(effectiveFeature, {
          hasCompany: true,
          hasStartup: true,
          hasEvaluator: true,
        })
      ),
      ...meetingRoomCompanyItems,
      ...menuEvaluatorsItems,
      ...configMenu,
    ];

    // Remove duplicate objects from the array (gambiarra da gambiarra)
    return menuItems.filter(
      (item, index, self) =>
        index ===
        self.findIndex((t) => t.title === item.title && t.path === item.path) &&
        !(item.icon && Array.isArray(item.icon) && item.icon[1] === "fa-reply") &&
        !(item.icon && Array.isArray(item.icon) && item.icon[1] === "star-half-stroke")
    );
    //   // Super Admin Profile Menu [Disabled for now

    //   if (!Array.isArray(profiles) || profiles.length === 0) {
    //     return []
    //   }
    //   const mapRoleToFeatures = (roleName?: string): string[] => {
    //     const role = String(roleName ?? '').toLowerCase()
    //     const out: string[] = []
    //     if (role.includes('admin')) out.push('challenge')
    //     if (role.includes('avaliador') || role.includes('evaluator')) out.push('evaluation')
    //     if (role.includes('startup') || role.includes('proponente') || role.includes('resolver')) out.push('idea')
    //     return out
    //   }

    //   const profileItems = profiles.map((p: any) => {
    //     const title = p?.company?.contactName
    //     const id = p?.id
    //     const isOpen = cookies.get('_selectedProfileOpenId') === id
    //     const featuresForProfile = mapRoleToFeatures(p?.role?.name)
    //     let subMenuItems: any[] = []
    //     if (featuresForProfile.includes('challenge')) subMenuItems = [...subMenuItems, ...companyMenu]
    //     if (featuresForProfile.includes('idea')) subMenuItems = [...subMenuItems, ...startupMenu]
    //     if (featuresForProfile.includes('evaluation')) subMenuItems = [...subMenuItems, ...evaluatorMenu]
    //     subMenuItems = [...subMenuItems, ...configMenu]

    //     subMenuItems = subMenuItems.map((it) => {
    //       const normalized = {
    //         title: it.title,
    //         path: it.path ?? currentLocation,
    //         subMenuIcon: it.icon,
    //         active: it.active,
    //         disabled: it.disabled,
    //         isProfile: true,
    //         onClick: ((orig) => () => {
    //           cookies.set('_selectedProfileName', title)
    //           if (typeof orig === 'function') orig()
    //         })(it?.onClick)
    //       }
    //       return normalized
    //     })

    //     return {
    //       title,
    //       icon: ['fas', 'user'],
    //       active: false,
    //       path: currentLocation,
    //       isSubMenu: true,
    //       subMenuItems,
    //       accordionOpen: Boolean(isOpen),
    //       onClick: () => {
    //         if (cookies.get('_selectedProfileOpenId') === id) {
    //           removeCookie('_selectedProfileOpenId')
    //           cookies.set('_selectedProfileName', '')
    //         } else {
    //           cookies.set('_selectedProfileOpenId', id)
    //           cookies.set('_selectedProfileName', title)
    //         }
    //       }
    //     }
    //   })

    //   return profileItems
  }

  return removeDuplicatedMenuItems(menuItems);
}
